import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TRANSLATIONS = ["KJV", "WEB", "ASV"];

export default function WordSearch() {
  const [q, setQ] = useState("");
  const [translation, setTranslation] = useState("KJV");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setData(null);
    try {
      const r = await client.get("/bible/search", { params: { q: q.trim(), translation } });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  // group results by book
  const byBook = data?.results?.reduce((acc, r) => {
    (acc[r.book] ||= []).push(r);
    return acc;
  }, {}) || {};

  return (
    <div className="space-y-10" data-testid="word-search-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#78350F] mb-4">Word Search</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-stone-900 mb-3">Every occurrence.</h1>
        <p className="text-stone-600 max-w-2xl">Type any English word from Scripture. We'll find every verse in which it appears and, in KJV, link the underlying Hebrew or Greek term.</p>
      </div>

      <form onSubmit={search} className="border-t border-b hairline py-8">
        <div className="flex flex-col md:flex-row items-stretch md:items-end gap-6">
          <div className="flex-1">
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">The English word</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. love, righteousness, grace"
              data-testid="word-search-input"
              className="w-full bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] focus:outline-none px-0 py-2 text-3xl font-serif-display" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">Translation</label>
            <select value={translation} onChange={(e) => setTranslation(e.target.value)}
              data-testid="word-search-translation"
              className="bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] focus:outline-none py-2 pr-6 text-lg font-serif-display">
              {TRANSLATIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button type="submit" disabled={busy} data-testid="word-search-submit"
            className="bg-[#9A3B3B] hover:bg-[#7F2F2F] text-white px-8 py-3 tracking-wide transition-colors disabled:opacity-60 flex items-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} strokeWidth={1.5} />}
            Search
          </button>
        </div>
      </form>

      {busy && (
        <div className="text-center py-12 text-stone-500" data-testid="word-search-loading">
          <Loader2 className="mx-auto animate-spin mb-3" size={24} />
          Searching Scripture...
        </div>
      )}

      {data && !busy && (
        <div className="space-y-10" data-testid="word-search-results">
          {/* Summary */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="border hairline bg-[#FDFBF7] p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3">Occurrences</div>
              <div className="font-serif-display text-6xl text-[#9A3B3B]" data-testid="word-search-count">{data.total}</div>
              <div className="text-sm text-stone-600 mt-2">verses contain "{data.query}"</div>
            </div>
            <div className="border hairline bg-[#FDFBF7] p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3">Books</div>
              <div className="font-serif-display text-6xl text-[#2E4F4F]">{Object.keys(byBook).length}</div>
              <div className="text-sm text-stone-600 mt-2">distinct books</div>
            </div>
            <div className="border hairline bg-[#FDFBF7] p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3">Translation</div>
              <div className="font-serif-display text-6xl text-stone-900">{data.translation}</div>
              <div className="text-sm text-stone-600 mt-2">
                {data.translation === "KJV" ? "Hebrew/Greek Strong's available" : "Text-only"}
              </div>
            </div>
          </div>

          {/* Results by book */}
          <div className="space-y-8">
            {Object.entries(byBook).map(([book, rows]) => (
              <div key={book} className="border-t hairline pt-6">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="font-serif-display text-2xl text-stone-900">{book}</h3>
                  <span className="text-xs uppercase tracking-[0.2em] text-stone-500">{rows.length} verses</span>
                </div>
                <ul className="space-y-3">
                  {rows.map((r, i) => (
                    <li key={i} className="flex gap-4 items-baseline group">
                      <Link
                        to={`/verse?book=${r.book_id}&chapter=${r.chapter}&verse=${r.verse}&translation=${data.translation}`}
                        data-testid={`result-${r.book_id}-${r.chapter}-${r.verse}`}
                        className="font-mono text-xs text-[#9A3B3B] whitespace-nowrap min-w-[70px] hover:underline">
                        {r.chapter}:{r.verse}
                      </Link>
                      <p className="text-stone-700 leading-relaxed"
                         dangerouslySetInnerHTML={{
                           __html: r.text.replace(new RegExp(`\\b(${escapeRegex(data.query)})\\b`, "gi"),
                             '<mark class="bg-amber-100/60 text-stone-900 px-0.5">$1</mark>')
                         }} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
